/**
 * Searchable nested-taxonomy picker.
 *
 * Fetches the flat taxonomy rows for a given `kind` (from
 * `/api/taxonomy/:kind`) and renders each row with its full breadcrumb
 * label (e.g. "Industrial › Cable › Extrusion › Line A › Stage 2") so
 * admins can see the full path when they've nested 3+ levels deep.
 *
 * Selection state is the leaf `label` (kept compatible with the
 * existing string-valued `type` / `category` fields on work orders and
 * assets). When an exact label match spans multiple parents the picker
 * surfaces both so the admin can disambiguate visually via the path.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Search } from 'lucide-react';

import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import { listTaxonomy } from '../services/api';

/**
 * Build the parent → children index. We deliberately keep the items
 * grouped (rather than flattened) so the picker can render a tree
 * with expand/collapse behaviour: only roots are visible by default;
 * children appear when the engineer taps the parent row's chevron.
 */
const buildIndex = (items) => {
  const byParent = new Map();
  const ids = new Set(items.map((i) => i.id));
  for (const it of items) {
    const pid = it.parentId && ids.has(it.parentId) ? it.parentId : null;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(it);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label));
  }
  return byParent;
};

/**
 * Flat list of {id, label, trail} — used for search-mode where the
 * tree collapses into a flat hit list (a search query implies the
 * user wants to see everything that matches, not navigate a tree).
 */
const flattenAll = (byParent) => {
  const out = [];
  const walk = (parentId, trail) => {
    for (const node of (byParent.get(parentId) || [])) {
      const nextTrail = [...trail, node.label];
      out.push({ id: node.id, label: node.label, trail: nextTrail });
      walk(node.id, nextTrail);
    }
  };
  walk(null, []);
  return out;
};

export const TaxonomyPicker = ({
  kind, value, onChange, placeholder = 'Select…',
  allowClear = true, testId,
}) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Tracks which parent IDs the engineer has expanded. Empty set =
  // every category starts collapsed (only root nodes visible) until
  // tapped. Reset on every popover open so the engineer sees a clean
  // tree each time.
  const [expanded, setExpanded] = useState(() => new Set());

  // Fetch on first mount so the trigger label can resolve.
  // Re-fetch every time the popover opens so engineers pick up the
  // latest categories the admin added without needing to reload — the
  // taxonomy list is small (a few KB), this is cheap.
  const refetch = React.useCallback(() => {
    listTaxonomy(kind).then((rows) => setItems(rows || [])).catch(() => {});
  }, [kind]);

  useEffect(() => { refetch(); }, [refetch]);
  useEffect(() => {
    if (open) {
      refetch();
      setExpanded(new Set());
      setSearch('');
    }
  }, [open, refetch]);

  const byParent = useMemo(() => buildIndex(items), [items]);
  const flat = useMemo(() => flattenAll(byParent), [byParent]);

  const currentRow = useMemo(
    () => flat.find((r) => r.label === value) || null,
    [flat, value],
  );

  // When searching: flatten all matches into a single hit list (no
  // tree navigation — engineer wants everything that matches now).
  const searchHits = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return flat.filter((r) => r.trail.join(' › ').toLowerCase().includes(q));
  }, [flat, search]);

  const pick = (label) => {
    onChange(label);
    setOpen(false);
    setSearch('');
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /** Recursive tree row renderer — only renders a parent's children
   *  when its id is in the `expanded` set. */
  const renderTreeRows = (parentId, depth) => {
    const children = byParent.get(parentId) || [];
    const out = [];
    for (const node of children) {
      const hasKids = (byParent.get(node.id) || []).length > 0;
      const isOpen = expanded.has(node.id);
      const isSelected = node.label === value;
      out.push(
        <div key={node.id} className="flex items-center">
          {hasKids ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleExpand(node.id); }}
              className="shrink-0 h-9 w-7 flex items-center justify-center text-slate-400 hover:text-slate-700"
              data-testid={testId ? `${testId}-toggle-${node.id}` : undefined}
              aria-label={isOpen ? 'Collapse' : 'Expand'}
            >
              <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
            </button>
          ) : (
            <span className="shrink-0 w-7" />
          )}
          <button
            type="button"
            onClick={() => pick(node.label)}
            className={`flex-1 text-left px-2 py-2 text-sm flex items-center gap-2 ${
              isSelected ? 'bg-blue-50 text-blue-900' : 'hover:bg-slate-50 text-slate-700'
            }`}
            style={{ paddingLeft: `${depth * 14 + 8}px` }}
            data-testid={testId ? `${testId}-row-${node.id}` : undefined}
          >
            <Check className={`h-3.5 w-3.5 shrink-0 ${isSelected ? 'text-blue-600' : 'text-transparent'}`} />
            <span className={`truncate flex-1 ${isSelected ? 'font-semibold' : 'font-normal'}`}>{node.label}</span>
          </button>
        </div>,
      );
      if (hasKids && isOpen) {
        out.push(...renderTreeRows(node.id, depth + 1));
      }
    }
    return out;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="mt-1.5 w-full justify-between h-10 font-normal text-left"
          data-testid={testId}
        >
          <span className={value ? 'text-slate-900 truncate' : 'text-slate-400'}>
            {currentRow ? currentRow.trail.join(' › ') : (value || placeholder)}
          </span>
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[min(28rem,calc(100vw-2rem))]" align="start">
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
          <Search className="h-4 w-4 text-slate-400 shrink-0" />
          <Input
            autoFocus value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="h-7 border-none focus-visible:ring-0 shadow-none px-0"
            data-testid={testId ? `${testId}-search` : undefined}
          />
          {allowClear && value && (
            <button
              type="button"
              onClick={() => pick('')}
              className="text-xs text-slate-500 hover:text-slate-900 shrink-0"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              Clear
            </button>
          )}
        </div>
        {/* Scroll container — `overscroll-contain` prevents iOS Safari
            from scrolling the page underneath when the engineer
            reaches the top/bottom of the list; `touch-action: pan-y`
            ensures vertical drags scroll the list rather than the
            page. `max-h-[60vh]` lets the list stretch to most of the
            viewport on phones (was max-h-72 ≈ 4 rows on iPhone). */}
        <div
          className="max-h-[60vh] overflow-y-auto py-1 overscroll-contain"
          style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
          data-testid={testId ? `${testId}-list` : undefined}
        >
          {searchHits !== null ? (
            // Search mode — flat hit list with breadcrumb context.
            searchHits.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-slate-500">No matches.</div>
            ) : (
              searchHits.map((r) => {
                const isSelected = r.label === value;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => pick(r.label)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                      isSelected ? 'bg-blue-50 text-blue-900' : 'hover:bg-slate-50 text-slate-700'
                    }`}
                    data-testid={testId ? `${testId}-row-${r.id}` : undefined}
                  >
                    <Check className={`h-3.5 w-3.5 shrink-0 ${isSelected ? 'text-blue-600' : 'text-transparent'}`} />
                    <span className="truncate flex-1">
                      {r.trail.slice(0, -1).length > 0 && (
                        <span className="text-slate-400 text-xs">{r.trail.slice(0, -1).join(' › ')} ›{' '}</span>
                      )}
                      <span className={isSelected ? 'font-semibold' : 'font-normal'}>{r.label}</span>
                    </span>
                  </button>
                );
              })
            )
          ) : (
            // Tree mode — collapsed-by-default, tap to expand.
            (byParent.get(null) || []).length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-slate-500">No options yet.</div>
            ) : renderTreeRows(null, 0)
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TaxonomyPicker;
